import { CommunityFeature } from "@upsquad/shared";
import { Calendar, type LucideIcon, MessageCircle, Newspaper, UserRound, Users, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EyebrowBadge } from "../atoms/eyebrow-badge";
import { SectionHeading } from "../atoms/section-heading";

interface LandingFeaturesSectionProps {
	communityName: string;
	features: { feature: CommunityFeature; enabled: boolean }[];
}

interface FeatureCard {
	key: CommunityFeature;
	icon: LucideIcon;
}

const FEATURES: FeatureCard[] = [
	{ key: CommunityFeature.FEED, icon: Newspaper },
	{ key: CommunityFeature.MESSAGES, icon: MessageCircle },
	{ key: CommunityFeature.MEMBERS, icon: Users },
	{ key: CommunityFeature.MEETINGS, icon: UserRound },
	{ key: CommunityFeature.EVENTS, icon: Calendar },
	{ key: CommunityFeature.VIDEOS, icon: Video },
];

export function LandingFeaturesSection({ communityName, features }: LandingFeaturesSectionProps) {
	const { t } = useTranslation("community");

	const enabledSet = new Set(features.filter((f) => f.enabled).map((f) => f.feature));
	const visibleFeatures = FEATURES.filter(({ key }) => enabledSet.has(key));

	if (visibleFeatures.length === 0) return null;

	return (
		<section id="features" className="bg-background py-24 md:py-32">
			<div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-16 px-6">
				<SectionHeading
					eyebrow={<EyebrowBadge tone="ink">{t("landing.features.eyebrow")}</EyebrowBadge>}
					title={t("landing.features.title")}
					titleAccent={t("landing.features.title_accent")}
					description={t("landing.features.description", { communityName })}
				/>
				<div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{visibleFeatures.map(({ key, icon: Icon }) => (
						<div
							key={key}
							className="landing-shadow-card landing-hover-lift flex flex-col gap-4 rounded-2xl border border-divider bg-content1 p-6"
						>
							<div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
								<Icon className="h-5 w-5" />
							</div>
							<h3 className="text-lg font-semibold text-foreground">{t(`landing.features.${key}.title`)}</h3>
							<p className="text-sm leading-relaxed text-foreground/60">{t(`landing.features.${key}.description`)}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
